/**
 * Cyzor Control API Integration Service
 * Base URL: configured via ENV.API_BASE_URL
 * Prefix: /api
 */

import { ENV } from '../config/environment';

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
}

export interface UserMe {
  id: string;
  email: string;
  name: string;
  role?: string;
  photoUrl?: string;
}

export interface Member {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
}

export interface Workspace {
  id: string;
  name: string;
  description?: string;
  segment?: string;
}

export interface Customer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  status?: string;
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  status: string;
  priority: string;
  dueDate?: string;
  budget?: string;
}

export interface Task {
  id: string;
  projectId: string;
  name: string;
  status: string; // 'A Fazer' | 'Em Progresso' | 'Revisão' | 'Concluído'
  dueDate?: string;
}

class ControlApiService {
  private get baseUrl(): string {
    return ENV.API_BASE_URL;
  }
  private apiPrefix = '/api';

  // Helper to retrieve token from localStorage
  private getAccessToken(): string | null {
    return localStorage.getItem('cyzor_access_token') || localStorage.getItem('cyzor_auth_token');
  }

  // Helper to retrieve API mode (live vs mock)
  public getApiMode(): 'live' | 'mock' {
    const mode = localStorage.getItem('cyzor_api_mode');
    return (mode === 'mock' ? 'mock' : 'live');
  }

  public setApiMode(mode: 'live' | 'mock') {
    localStorage.setItem('cyzor_api_mode', mode);
    window.dispatchEvent(new Event('cyzor_api_mode_changed'));
  }

  // Get full endpoint URL
  private getUrl(endpoint: string, absolute = false): string {
    if (absolute) {
      return `${this.baseUrl}${endpoint}`;
    }
    return `${this.baseUrl}${this.apiPrefix}${endpoint}`;
  }

  // Generic request handler
  private async request<T>(endpoint: string, options: RequestInit = {}, absolute = false): Promise<T> {
    const url = this.getUrl(endpoint, absolute);
    const token = this.getAccessToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401) {
        this.clearSession();
        window.dispatchEvent(new CustomEvent('cyzor_unauthorized'));
      }
      throw new Error(errorData.message || errorData.error || `Erro na requisição à API (${response.status})`);
    }

    return response.json() as Promise<T>;
  }

  // 1. Health check (Public)
  public async checkHealth(): Promise<{ status: string; online: boolean }> {
    try {
      const res = await fetch(`${this.baseUrl}/health`);
      if (res.ok) {
        const data = await res.json();
        return { status: data.status || 'ok', online: true };
      }
      return { status: 'error', online: false };
    } catch (e) {
      return { status: 'offline', online: false };
    }
  }

  // 2. Authentication
  public async register(email: string, pass: string, fullName: string, organizationName: string): Promise<AuthResponse> {
    const data = await this.request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password: pass, fullName, organizationName }),
    });
    if (data.accessToken) {
      localStorage.setItem('cyzor_access_token', data.accessToken);
      localStorage.setItem('cyzor_refresh_token', data.refreshToken);
      this.setApiMode('live');
    }
    return data;
  }

  public async login(email: string, pass: string): Promise<AuthResponse> {
    const data = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password: pass }),
    });
    if (data.accessToken) {
      localStorage.setItem('cyzor_access_token', data.accessToken);
      localStorage.setItem('cyzor_refresh_token', data.refreshToken);
      this.setApiMode('live');
    }
    return data;
  }

  public logout() {
    this.clearSession();
  }

  private clearSession() {
    localStorage.removeItem('cyzor_access_token');
    localStorage.removeItem('cyzor_auth_token');
    localStorage.removeItem('cyzor_refresh_token');
    localStorage.removeItem('cyzor_refresh_token_legacy');
    localStorage.removeItem('cyzor_user');
    localStorage.removeItem('cyzor_organization');
    localStorage.removeItem('cyzor_roles');
    localStorage.removeItem('cyzor_permissions');
  }

  // 3. User & Members
  public async getMe(): Promise<UserMe> {
    return this.request<UserMe>('/users/me');
  }

  public async getMembers(): Promise<Member[]> {
    return this.request<Member[]>('/users/members');
  }

  // 4. Workspaces
  public async getWorkspaces(): Promise<Workspace[]> {
    return this.request<Workspace[]>('/workspaces');
  }

  public async createWorkspace(name: string, description?: string, segment?: string): Promise<Workspace> {
    return this.request<Workspace>('/workspaces', {
      method: 'POST',
      body: JSON.stringify({ name, description, segment }),
    });
  }

  // 5. Customers
  public async getCustomers(): Promise<Customer[]> {
    return this.request<Customer[]>('/customers');
  }

  public async createCustomer(name: string, email?: string, phone?: string, status = 'Ativo'): Promise<Customer> {
    return this.request<Customer>('/customers', {
      method: 'POST',
      body: JSON.stringify({ name, email, phone, status }),
    });
  }

  // 6. Projects
  public async getProjects(): Promise<Project[]> {
    return this.request<Project[]>('/projects');
  }

  public async createProject(name: string, priority = 'Média', dueDate?: string, status = 'planejamento', budget = '0'): Promise<Project> {
    return this.request<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify({ name, priority, dueDate, status, budget }),
    });
  }

  // 7. Tasks
  public async getTasks(): Promise<Task[]> {
    return this.request<Task[]>('/tasks');
  }

  public async createTask(projectId: string, name: string, status = 'A Fazer', dueDate?: string): Promise<Task> {
    return this.request<Task>('/tasks', {
      method: 'POST',
      body: JSON.stringify({ projectId, name, status, dueDate }),
    });
  }
}

export const controlApi = new ControlApiService();
